import {
	getSelectedLocation,
	setSelectedLocation,
	updateLocationSelector,
} from "./location-state.js";

function safeJsonParse(value, fallback = null) {
	if (!value) {
		return fallback;
	}

	try {
		return JSON.parse(value);
	} catch (error) {
		console.error("Failed to parse JSON payload", error);
		return fallback;
	}
}

const selectedLocation = getSelectedLocation();
const moduleData = safeJsonParse(
	document.getElementById("wp-script-module-data-@locations")?.innerHTML,
	{},
);
const franchiseLocations = Array.isArray(moduleData.franchise_locations)
	? moduleData.franchise_locations
	: [];
console.log(franchiseLocations);
const siteUrl = moduleData.site_url;
const googleMapsApiKey = moduleData.frontend_google_maps_api_key;
const primaryColor = getComputedStyle(
	document.documentElement,
).getPropertyValue("--accent");
const secondaryColor = getComputedStyle(
	document.documentElement,
).getPropertyValue("--accent-2");
const primaryColorLight = getComputedStyle(
	document.documentElement,
).getPropertyValue("--accent-light");
const secondaryColorLight = getComputedStyle(
	document.documentElement,
).getPropertyValue("--accent-2-light");
const iconsObject = moduleData.icons;

function hasValidCoordinates(location) {
	return (
		Number.isFinite(Number.parseFloat(location?.latitude)) &&
		Number.isFinite(Number.parseFloat(location?.longitude))
	);
}

const renderableLocations = franchiseLocations.filter(hasValidCoordinates);
const skippedLocationCount = franchiseLocations.length - renderableLocations.length;
if (skippedLocationCount > 0) {
	console.warn(
		`Skipping ${skippedLocationCount} location(s) without valid latitude/longitude`,
	);
}

const resolvedSelectedLocation = selectedLocation
	? (renderableLocations.find(
			(location) => String(location.id) === String(selectedLocation.id),
		) ?? null)
	: null;
const defaultLocation =
	resolvedSelectedLocation ?? renderableLocations[0] ?? null;

const ui = {
	map: null,
	currentInfoWindow: null,
	currentSelectedItem: null,
	entries: [],
	locationsLayout: null,
	geocoder: null,
	searchRequestId: 0,
};

const US_STATE_NAMES = {
	AL: "alabama",
	AK: "alaska",
	AZ: "arizona",
	AR: "arkansas",
	CA: "california",
	CO: "colorado",
	CT: "connecticut",
	DE: "delaware",
	FL: "florida",
	GA: "georgia",
	HI: "hawaii",
	ID: "idaho",
	IL: "illinois",
	IN: "indiana",
	IA: "iowa",
	KS: "kansas",
	KY: "kentucky",
	LA: "louisiana",
	ME: "maine",
	MD: "maryland",
	MA: "massachusetts",
	MI: "michigan",
	MN: "minnesota",
	MS: "mississippi",
	MO: "missouri",
	MT: "montana",
	NE: "nebraska",
	NV: "nevada",
	NH: "new hampshire",
	NJ: "new jersey",
	NM: "new mexico",
	NY: "new york",
	NC: "north carolina",
	ND: "north dakota",
	OH: "ohio",
	OK: "oklahoma",
	OR: "oregon",
	PA: "pennsylvania",
	RI: "rhode island",
	SC: "south carolina",
	SD: "south dakota",
	TN: "tennessee",
	TX: "texas",
	UT: "utah",
	VT: "vermont",
	VA: "virginia",
	WA: "washington",
	WV: "west virginia",
	WI: "wisconsin",
	WY: "wyoming",
	DC: "district of columbia",
};

function getLocationCenter(location) {
	return {
		lat: parseFloat(location.latitude),
		lng: parseFloat(location.longitude),
	};
}

function isSelectedLocation(location) {
	return (
		Boolean(resolvedSelectedLocation) &&
		String(location.id) === String(resolvedSelectedLocation.id)
	);
}

function getLocationSlug(location) {
	return location.city.toLowerCase().replace(/'/g, "").replace(/ /g, "-");
}

function normalizeQuery(value) {
	return value
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, " ")
		.replace(/\s+/g, " ")
		.trim();
}

function matchesSearch(location, query) {
	if (!query) {
		return true;
	}

	return [
		location.name,
		location.address,
		location.city,
		location.state,
		location.zip,
	]
		.filter(Boolean)
		.some((value) => value.toLowerCase().includes(query));
}

function getQueryTokens(query) {
	return normalizeQuery(query).split(" ").filter(Boolean);
}

function getLocationStateNames(location) {
	const stateCode = String(location.state ?? "")
		.trim()
		.toUpperCase();
	const stateName =
		US_STATE_NAMES[stateCode] ?? normalizeQuery(location.state ?? "");

	return {
		stateCode: normalizeQuery(stateCode),
		stateName,
	};
}

function getLocationSearchPhrases(location) {
	const city = normalizeQuery(location.city ?? "");
	const address = normalizeQuery(location.address ?? "");
	const zip = normalizeQuery(location.zip ?? "");
	const name = normalizeQuery(location.name ?? "");
	const { stateCode, stateName } = getLocationStateNames(location);

	return [
		name,
		city,
		address,
		zip,
		stateCode,
		stateName,
		`${city} ${stateCode}`.trim(),
		`${city} ${stateName}`.trim(),
		`${address} ${city} ${stateCode} ${zip}`.trim(),
		`${address} ${city} ${stateName} ${zip}`.trim(),
	].filter(Boolean);
}

function rankChainLocationMatch(location, rawQuery) {
	const normalizedQuery = normalizeQuery(rawQuery);
	if (!normalizedQuery) {
		return null;
	}

	const tokens = getQueryTokens(rawQuery);
	const phrases = getLocationSearchPhrases(location);
	const city = normalizeQuery(location.city ?? "");
	const address = normalizeQuery(location.address ?? "");
	const zip = normalizeQuery(location.zip ?? "");
	const name = normalizeQuery(location.name ?? "");
	const { stateCode, stateName } = getLocationStateNames(location);
	const cityWithStateCode = `${city} ${stateCode}`.trim();
	const cityWithStateName = `${city} ${stateName}`.trim();
	const allTokensMatched = tokens.every((token) =>
		phrases.some((phrase) => phrase.includes(token)),
	);

	if (!allTokensMatched) {
		return null;
	}

	let score = 0;
	let matchType = "contains";

	if (
		normalizedQuery === city ||
		normalizedQuery === cityWithStateCode ||
		normalizedQuery === cityWithStateName ||
		normalizedQuery === zip
	) {
		score += 1000;
		matchType = "exact";
	} else if (normalizedQuery === name) {
		score += 900;
		matchType = "exact";
	} else if (
		city.startsWith(normalizedQuery) ||
		cityWithStateCode.startsWith(normalizedQuery) ||
		cityWithStateName.startsWith(normalizedQuery)
	) {
		score += 700;
		matchType = "prefix";
	} else if (
		address.startsWith(normalizedQuery) ||
		name.startsWith(normalizedQuery)
	) {
		score += 600;
		matchType = "prefix";
	} else {
		score += 450;
		matchType = "token";
	}

	score += tokens.length * 25;
	score -= city.length * 0.01;

	return {
		location,
		score,
		matchType,
	};
}

function getChainAwareMatches(rawQuery) {
	return franchiseLocations
		.map((location) => rankChainLocationMatch(location, rawQuery))
		.filter(Boolean)
		.sort((left, right) => right.score - left.score);
}

function looksLikeAddressQuery(rawQuery) {
	return /\d/.test(rawQuery) || rawQuery.includes(",");
}

function getDistanceBetweenPoints(origin, destination) {
	const toRadians = (value) => (value * Math.PI) / 180;
	const earthRadiusMeters = 6371000;
	const latitudeDelta = toRadians(destination.lat - origin.lat);
	const longitudeDelta = toRadians(destination.lng - origin.lng);
	const originLatitude = toRadians(origin.lat);
	const destinationLatitude = toRadians(destination.lat);

	const a =
		Math.sin(latitudeDelta / 2) ** 2 +
		Math.cos(originLatitude) *
			Math.cos(destinationLatitude) *
			Math.sin(longitudeDelta / 2) ** 2;

	return 2 * earthRadiusMeters * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function renderEntries(entries) {
	if (!ui.locationsLayout) {
		return;
	}

	entries.forEach((entry) => {
		ui.locationsLayout.appendChild(entry.locationItem);
	});
}

function showEntries(visibleEntries) {
	closeCurrentInfoWindow();

	const visibleIds = new Set(
		visibleEntries.map((entry) => String(entry.location.id)),
	);
	const hiddenEntries = ui.entries
		.filter((entry) => !visibleIds.has(String(entry.location.id)))
		.sort((a, b) => a.originalIndex - b.originalIndex);

	ui.entries.forEach((entry) => {
		const visible = visibleIds.has(String(entry.location.id));
		entry.locationItem.style.display = visible ? "" : "none";
		entry.marker.map = visible ? ui.map : null;
	});

	renderEntries([...visibleEntries, ...hiddenEntries]);
}

function resetSearchResults() {
	showEntries(
		[...ui.entries].sort((a, b) => a.originalIndex - b.originalIndex),
	);
	setStatus("Browse the map or pick a store from the list.");
}

function geocodeAddress(query) {
	return new Promise((resolve, reject) => {
		if (!ui.geocoder) {
			ui.geocoder = new google.maps.Geocoder();
		}

		const bounds = new google.maps.LatLngBounds();
		franchiseLocations.forEach((location) => {
			const center = getLocationCenter(location);
			if (!Number.isNaN(center.lat) && !Number.isNaN(center.lng)) {
				bounds.extend(center);
			}
		});

		ui.geocoder.geocode(
			{
				address: query,
				componentRestrictions: { country: "US" },
				bounds,
			},
			(results, status) => {
				if (status !== "OK" || !results?.length) {
					reject(new Error(`Geocode failed: ${status}`));
					return;
				}

				resolve(results[0]);
			},
		);
	});
}

async function applySearchLocation() {
	const searchInput = document.getElementById("locations-search");
	const rawQuery = searchInput?.value ?? "";
	const query = rawQuery.trim();

	if (!query) {
		resetSearchResults();
		return;
	}

	const requestId = ui.searchRequestId + 1;
	ui.searchRequestId = requestId;
	setStatus(`Searching for ${query}...`);

	const chainMatches = getChainAwareMatches(query);
	const queryTokens = getQueryTokens(query);
	const topChainMatch = chainMatches[0] ?? null;
	const shouldUseChainMatches =
		chainMatches.length > 0 &&
		(topChainMatch?.matchType === "exact" ||
			topChainMatch?.matchType === "prefix" ||
			(!looksLikeAddressQuery(query) &&
				queryTokens.length > 0 &&
				topChainMatch?.matchType === "token"));

	if (shouldUseChainMatches) {
		const matchedEntries = chainMatches
			.map((match) =>
				ui.entries.find(
					(entry) => String(entry.location.id) === String(match.location.id),
				),
			)
			.filter(Boolean);
		const primaryEntry = matchedEntries[0] ?? null;

		showEntries(matchedEntries);
		if (primaryEntry) {
			focusLocationEntry(primaryEntry);
		}

		setStatus(`Showing Teresa's Pizza locations matching "${query}".`);
		return;
	}

	try {
		const result = await geocodeAddress(query);
		if (requestId !== ui.searchRequestId) {
			return;
		}

		const searchLocation = {
			lat: result.geometry.location.lat(),
			lng: result.geometry.location.lng(),
		};

		const sortedEntries = [...ui.entries]
			.map((entry) => ({
				...entry,
				distanceToSearch: getDistanceBetweenPoints(
					searchLocation,
					getLocationCenter(entry.location),
				),
			}))
			.sort((a, b) => a.distanceToSearch - b.distanceToSearch);
		showEntries(sortedEntries);
		if (sortedEntries[0]) {
			focusLocationEntry(sortedEntries[0]);
		}
		setStatus(`Showing locations nearest to ${result.formatted_address}.`);
	} catch (error) {
		if (requestId !== ui.searchRequestId) {
			return;
		}

		let visibleCount = 0;
		const fallbackEntries = ui.entries.filter((entry) => {
			const visible = matchesSearch(entry.location, normalizeQuery(query));
			if (visible) {
				visibleCount += 1;
			}
			return visible;
		});

		showEntries(
			fallbackEntries.length
				? fallbackEntries
				: [...ui.entries].sort((a, b) => a.originalIndex - b.originalIndex),
		);
		if (fallbackEntries[0]) {
			focusLocationEntry(fallbackEntries[0]);
		}
		setStatus(
			visibleCount
				? `Showing Teresa's Pizza locations matching "${query}".`
				: `No locations found near "${query}".`,
		);
	}
}

function setStatus(message) {
	const status = document.getElementById("locations-toolbar-status");
	if (status) {
		status.textContent = message;
	}
}

function closeCurrentInfoWindow() {
	if (ui.currentInfoWindow) {
		ui.currentInfoWindow.close();
	}
	if (ui.currentSelectedItem) {
		ui.currentSelectedItem.classList.remove("selected");
	}
	ui.currentInfoWindow = null;
	ui.currentSelectedItem = null;
}

((g) => {
	var h,
		a,
		k,
		p = "The Google Maps JavaScript API",
		c = "google",
		l = "importLibrary",
		q = "__ib__",
		m = document,
		b = window;
	b = b[c] || (b[c] = {});
	var d = b.maps || (b.maps = {}),
		r = new Set(),
		e = new URLSearchParams(),
		u = () =>
			h ||
			(h = new Promise(async (f, n) => {
				await (a = m.createElement("script"));
				e.set("libraries", [...r] + "");
				for (k in g)
					e.set(
						k.replace(/[A-Z]/g, (t) => "_" + t[0].toLowerCase()),
						g[k],
					);
				e.set("callback", c + ".maps." + q);
				a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
				d[q] = f;
				a.onerror = () => (h = n(Error(p + " could not load.")));
				a.nonce = m.querySelector("script[nonce]")?.nonce || "";
				m.head.append(a);
			}));
	d[l]
		? console.warn(p + " only loads once. Ignoring:", g)
		: (d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n)));
})({ key: googleMapsApiKey, v: "weekly" });

async function fetchLocationDistances(userLocation) {
	const origin = `${userLocation.coords.latitude},${userLocation.coords.longitude}`;
	const destinations = renderableLocations
		.map((location) => `${location.latitude},${location.longitude}`)
		.join("|");
	const url = `/wp-json/teresas-pizza/v1/distance-matrix?origins=${encodeURIComponent(origin)}&destinations=${encodeURIComponent(destinations)}`;
	const response = await fetch(url);
	const data = await response.json();

	if (!response.ok || !data.rows || !data.rows[0] || !data.rows[0].elements) {
		throw new Error("Unable to load nearby locations");
	}

	return data.rows[0].elements
		.map((element, index) => ({
			location: renderableLocations[index],
			durationValue: element.duration
				? element.duration.value
				: Number.MAX_VALUE,
		}))
		.sort((a, b) => a.durationValue - b.durationValue);
}

function createInfoWindowContent(location) {
	return `
        <div class="location-image-container">
            <img class="location-img" src="${location.primary_image_url}" alt="${location.name}">
        </div>
        <div class="location-info-content">
            <div class="location-info-header">
                ${isSelectedLocation(location) ? '<p class="location-info-selected location-item-selected-title">Your Location</p>' : ""}
                <h3 class="location-info-name"><a href="${siteUrl}/${getLocationSlug(location)}">${location.name}</a></h3>
            </div>
            <div class="location-info-address-container">
                <p class="location-info-address">${location.address}</p>
                <p class="location-info-address">${location.city}, ${location.state} ${location.zip}</p>
            </div>
            <p class="location-info-phone">${location.phone}</p>
            <div class="navigation-actions">
                <a target="_blank" href="https://google.com/maps/place/${location.address.replace(/ /g, "+")},+${location.city.replace(/ /g, "+")},+${location.state.replace(/ /g, "+")},+${location.zip.replace(/ /g, "+")}" class="location-info-action"><img src="${iconsObject.googlemaps}" alt="Google Maps"></a>
                <a target="_blank" href="https://waze.com/ul?q=${encodeURI(location.address)}%20${encodeURI(location.city)}%20${encodeURI(location.state)}%20${encodeURI(location.zip)}" class="location-info-action"><img src="${iconsObject.waze}" alt="Waze"></a>
                <a target="_blank" href="https://maps.apple.com/daddr=${location.address.replace(/ /g, "+")},+${location.city.replace(/ /g, "+")},+${location.state.replace(/ /g, "+")},+${location.zip.replace(/ /g, "+")}" class="location-info-action"><img src="${iconsObject.applemaps}" alt="Apple Maps"></a>
            </div>
            ${
							isSelectedLocation(location)
								? ""
								: `
            <div class="location-info-actions">
                <button class="change-my-location" type="button">Make this my location</button>
            </div>
            `
						}
        </div>
    `;
}

function buildHoursDisplay(location) {
	const today = new Date()
		.toLocaleDateString("en-US", { weekday: "long" })
		.toLowerCase();
	const locationHours = safeJsonParse(location.hours, {});
	const todaySchedule = locationHours[today];

	if (!todaySchedule || todaySchedule.closed || !todaySchedule.ranges?.length) {
		return "Closed";
	}

	const { open, close } = todaySchedule.ranges[0];
	return `${convertTo12HourFormat(open)} - ${convertTo12HourFormat(close)}`;
}

function createSelectedLocationCard(location, hoursDisplay) {
	const locationItem = document.createElement("div");
	locationItem.classList.add("location-item", "location-item-selected");
	locationItem.innerHTML = `
        <div class="location-item-content grid-2-col">
            <div class="location-item-img grid-col">
                ${location.primary_image_url ? `<img class="location-img selected" src="${location.primary_image_url}" alt="${location.name}">` : ""}
            </div>
            <div class="location-item-inner selected grid-col">
                <h4 class="location-item-selected-title">Your Location</h4>
                <div class="location-item-data selected">
                    <h4 class="location-item-name"><a href="${siteUrl}/${getLocationSlug(location)}">${location.name}</a></h4>
                    <div class="location-item-address-container">
                        <p class="location-item-address">${location.address}</p>
                        <p class="location-item-address">${location.city}, ${location.state} ${location.zip}</p>
                    </div>
                    <div class="location-item-hours-container">
                        <p class="location-item-hours-label">Today's Hours</p>
                        <p class="hours-display">${hoursDisplay}</p>
                    </div>
                    <p class="location-item-phone">${location.phone}</p>
                </div>
                <div class="location-item-actions selected-actions">
                    <a href="tel:+1${location.phone.replace(/\D/g, "")}" class="location-item-action-text button-small accent">Call</a>
                    <a href="${siteUrl}/${getLocationSlug(location)}/menu" class="location-item-action-text button-small accent-2">View Menu</a>
                </div>
            </div>
        </div>
    `;
	return locationItem;
}

function createLocationCard(location, hoursDisplay) {
	const locationItem = document.createElement("article");
	locationItem.classList.add("location-item");
	locationItem.dataset.locationId = location.id;
	locationItem.innerHTML = `
        <div class="location-item-content">
            <div class="location-item-inner">
                <div class="location-item-column left-column">
                    <h4 class="location-name"><a href="${siteUrl}/${getLocationSlug(location)}">${location.name}</a></h4>
                    <div class="location-item-address-container">
                        <p class="location-item-address">${location.address}</p>
                        <p class="location-item-address">${location.city}, ${location.state} ${location.zip}</p>
                    </div>
                </div>
                <div class="location-item-column right-column">
                    <div class="location-item-actions">
                        <button class="find-location" type="button" aria-label="Show ${location.name} on map"><svg aria-hidden="true" role="img" height="1.5rem" width="1.5rem" viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z"></path></svg></button>
                        <a href="${siteUrl}/${getLocationSlug(location)}/menu" class="location-item-menu-link" aria-label="View ${location.name} menu"><svg aria-hidden="true" role="img" xmlns="http://www.w3.org/2000/svg" width="1.5rem" height="1.5rem" viewBox="0 0 20 20"><path d="M7 4.5c-.3 0-.5.3-.5.5v2.5h-1V5c0-.3-.2-.5-.5-.5s-.5.3-.5.5v2.5h-1V5c0-.3-.2-.5-.5-.5s-.5.3-.5.5v3.3c0 .9.7 1.6 1.5 1.7v7c0 .6.4 1 1 1s1-.4 1-1v-7c.8-.1 1.5-.8 1.5-1.7V5c0-.2-.2-.5-.5-.5zM9 5v6h1v6c0 .6.4 1 1 1s1-.4 1-1V2c-1.7 0-3 1.3-3 3zm7-1c-1.4 0-2.5 1.5-2.5 3.3-.1 1.2.5 2.3 1.5 3V17c0 .6.4 1 1 1s1-.4 1-1v-6.7c1-.7 1.6-1.8 1.5-3C18.5 5.5 17.4 4 16 4z"/></svg></a>
                        <a href="tel:+1${location.phone.replace(/\D/g, "")}" class="location-item-action" aria-label="Call ${location.name}"><svg aria-hidden="true" role="img" height="1.5rem" width="1.5rem" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M493.4 24.6l-104-24c-11.3-2.6-22.9 3.3-27.5 13.9l-48 112c-4.2 9.8-1.4 21.3 6.9 28l60.6 49.6c-36 76.7-98.9 140.5-177.2 177.2l-49.6-60.6c-6.8-8.3-18.2-11.1-28-6.9l-112 48C3.9 366.5-2 378.1.6 389.4l24 104C27.1 504.2 36.7 512 48 512c256.1 0 464-207.5 464-464 0-11.2-7.7-20.9-18.6-23.4z"></path></svg></a>
                    </div>
                    <div class="location-item-hours-container">
                        <p class="location-item-hours-label">Today's Hours</p>
                        <p class="hours-display">${hoursDisplay}</p>
                    </div>
                </div>
            </div>
        </div>
    `;
	return locationItem;
}

function focusLocationEntry(entry) {
	closeCurrentInfoWindow();
	entry.infoWindow.open({ anchor: entry.marker, map: ui.map });
	entry.locationItem.classList.add("selected");
	entry.locationItem.scrollIntoView({ behavior: "smooth", block: "nearest" });
	ui.currentInfoWindow = entry.infoWindow;
	ui.currentSelectedItem = entry.locationItem;
}

function bindInfoWindowActions(infoWindow, location) {
	google.maps.event.addListener(infoWindow, "domready", () => {
		const infoWindowContainer = document.querySelector(".gm-style-iw");
		const infoWindowContent =
			infoWindowContainer?.querySelector(".location-info");
		if (infoWindowContent) {
			infoWindowContent.addEventListener("click", (event) => {
				event.stopPropagation();
			});
		}

		const changeMyLocationButton = infoWindowContainer?.querySelector(
			".change-my-location",
		);
		if (changeMyLocationButton) {
			changeMyLocationButton.addEventListener("click", (event) => {
				event.preventDefault();
				event.stopPropagation();
				setSelectedLocation(location);
				updateLocationSelector();
				window.location.reload();
			});
		}
	});
}

async function generateLocationsLayout(locations) {
	if (!defaultLocation) {
		return;
	}

	const { Map } = await google.maps.importLibrary("maps");
	const { AdvancedMarkerElement, PinElement } =
		await google.maps.importLibrary("marker");

	ui.map = new Map(document.getElementById("map-item"), {
		zoom: 11,
		center: getLocationCenter(defaultLocation),
		mapId: "find-a-franchise-map",
	});

	const selectedLocationItem = document.getElementById(
		"selected-location-item",
	);
	const locationsListItem = document.getElementById("locations-list-item");
	const selectedSection = document.createElement("section");
	selectedSection.classList.add("user-selected-location-section");
	const locationsLayout = document.createElement("div");
	locationsLayout.classList.add("locations-grid");
	ui.locationsLayout = locationsLayout;

	if (resolvedSelectedLocation) {
		selectedSection.appendChild(
			createSelectedLocationCard(
				resolvedSelectedLocation,
				buildHoursDisplay(resolvedSelectedLocation),
			),
		);
		selectedLocationItem?.appendChild(selectedSection);
	}

	locations.forEach((location) => {
		if (!hasValidCoordinates(location)) {
			return;
		}

		const hoursDisplay = buildHoursDisplay(location);
		const locationItem = createLocationCard(location, hoursDisplay);
		const pin = new PinElement({
			background: isSelectedLocation(location) ? secondaryColor : primaryColor,
			borderColor: isSelectedLocation(location)
				? secondaryColorLight
				: primaryColorLight,
			glyphColor: "#fff",
			scale: isSelectedLocation(location) ? 1.4 : 1,
		});

		const marker = new AdvancedMarkerElement({
			map: ui.map,
			position: getLocationCenter(location),
			title: location.name,
			content: pin.element,
		});

		const infoWindow = new google.maps.InfoWindow({
			content: createInfoWindowContent(location),
			ariaLabel: location.name,
		});

		bindInfoWindowActions(infoWindow, location);

		const entry = {
			location,
			locationItem,
			marker,
			infoWindow,
			originalIndex: ui.entries.length,
		};
		ui.entries.push(entry);
		locationsLayout.appendChild(locationItem);

		const findLocationButton = locationItem.querySelector(".find-location");
		findLocationButton?.addEventListener("click", (event) => {
			event.preventDefault();
			event.stopPropagation();
			focusLocationEntry(entry);
		});

		marker.addListener("click", (event) => {
			focusLocationEntry(entry);
			event.stop();
		});

		locationItem.addEventListener("click", (event) => {
			if (event.target.closest("a") || event.target.closest("button")) {
				return;
			}

			event.preventDefault();
			window.location.href = `${siteUrl}/${getLocationSlug(location)}`;
		});

		infoWindow.addListener("closeclick", closeCurrentInfoWindow);
	});

	ui.map.addListener("click", closeCurrentInfoWindow);
	locationsListItem?.appendChild(locationsLayout);

	let searchDebounceTimer = null;
	document.getElementById("locations-search")?.addEventListener("input", () => {
		window.clearTimeout(searchDebounceTimer);
		searchDebounceTimer = window.setTimeout(() => {
			applySearchLocation();
		}, 250);
	});
	document
		.getElementById("locations-use-my-location")
		?.addEventListener("click", async () => {
			const button = document.getElementById("locations-use-my-location");
			button.disabled = true;
			button.textContent = "Finding...";
			setStatus("Looking for stores near you...");

			try {
				const cachedLocation = safeJsonParse(
					window.localStorage.getItem("teresasPizzaUserLocation"),
				);
				const userLocation =
					cachedLocation ??
					(await new Promise((resolve, reject) => {
						navigator.geolocation.getCurrentPosition(resolve, reject);
					}));
				const nearest = await fetchLocationDistances(userLocation);
				const nearestEntry = ui.entries.find(
					(entry) =>
						String(entry.location.id) === String(nearest[0]?.location.id),
				);
				if (nearestEntry) {
					focusLocationEntry(nearestEntry);
					setStatus(`Nearest store: ${nearestEntry.location.name}.`);
				}
			} catch (error) {
				setStatus(
					"We could not determine your location. Search by city or browse the map.",
				);
			} finally {
				button.disabled = false;
				button.textContent = "Use My Location";
			}
		});
}

async function initLocationsPage() {
	await generateLocationsLayout(renderableLocations);
	console.log("Locations layout finished");
}

initLocationsPage().catch((error) => {
	console.error("Failed to generate locations layout", error);
});

function convertTo12HourFormat(time) {
	const [hours, minutes] = time.split(":");
	const isPM = parseInt(hours, 10) >= 12;
	const formattedHours = parseInt(hours, 10) % 12 || 12;
	return `${formattedHours}:${minutes} ${isPM ? "PM" : "AM"}`;
}
